/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { FilterMetadata } from 'primeng/api';
import { Observable } from 'rxjs';
import { GlobalCommunication } from 'src/app/global-communication.service';
import { IPagingResultModel } from 'src/app/models/paging-result.model';

import { SpinnerCommunication } from '../shared/spinner/spinner.communication';
import { AppModuleInjector } from './app-module-injector';
import { AuthService } from './auth.service';
import { SettingsService } from './settings.service';

export abstract class ApiService {
  protected abstract getApiUrl(): string;

  protected http: HttpClient;
  protected authService: AuthService;
  protected router: Router;
  protected settings: SettingsService;
  protected spinnerCommunication: SpinnerCommunication;
  protected globalCommunication: GlobalCommunication;
  protected defaultApi: string; // = `${this.settings.baseUrl}/api/${this.apiUrl}`;

  constructor() {
    this.http = AppModuleInjector.injector.get(HttpClient);
    this.authService = AppModuleInjector.injector.get(AuthService);
    this.router = AppModuleInjector.injector.get(Router);
    this.settings = AppModuleInjector.injector.get(SettingsService);
    this.spinnerCommunication = AppModuleInjector.injector.get(SpinnerCommunication);
    this.globalCommunication = AppModuleInjector.injector.get(GlobalCommunication);
    this.defaultApi = `${this.settings.baseUrl}/api/${this.getApiUrl()}`;
  }

  protected getOptions(): any {
    let opts: HttpHeaders = new HttpHeaders();
    let accessToken = this.authService.getAccessToken();
    opts = opts.set('Authorization', `Bearer ${accessToken}`);
    opts = opts.append('Content-Type', 'application/json');
    return { headers: opts };
  }

  protected baseGet(link: string): Observable<any> {
    let opts = this.getOptions();
    let url = this.defaultApi + '/' + link;
    return this.http.get(url, opts);
  }

  protected baseGetDocument(link: string): Observable<any> {
    let options = this.getOptions();
    options.responseType = 'blob';
    return this.http.get(this.defaultApi + '/' + link, options);
  }

  protected baseGetById(id: number | string, link: string = ''): Observable<any> {
    let opts = this.getOptions();
    let url = this.defaultApi + '/' + id;
    if (link) {
      url += '/' + link;
    }
    return this.http.get(url, opts);
  }

  protected baseGetAll(customFilters: any): Observable<any> {
    let opts = this.getOptions();
    let url = this.defaultApi;
    if (customFilters) {
      let urlParams = '';
      Object.entries(customFilters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (urlParams.length > 0) {
              urlParams += '&';
            }
            urlParams += key + '=' + item;
          });
        } else if (value !== null) {
          if (urlParams.length > 0) {
            urlParams += '&';
          }
          urlParams += key + '=' + value;
        }
      });
      url += '?' + urlParams;
    }
    return this.http.get(url, opts);
  }

  protected basePost(data: any, link: string = ''): Observable<any> {
    let opts = this.getOptions();
    let sendData = JSON.stringify(data);
    let url = this.defaultApi;
    if (link) {
      url += '/' + link;
    }
    return this.http.post(url, sendData, opts);
  }

  protected basePut(id: number | string | null, data: any, link: string = ''): Observable<void> {
    let opts = this.getOptions();
    let sendData = JSON.stringify(data);
    let url = this.defaultApi;
    if (id) {
      url += '/' + id;
    }
    if (link) {
      url += '/' + link;
    }
    return this.http.put(url, sendData, opts) as any;
  }

  protected baseDelete(id: number | string | null, link: string = ''): Observable<void> {
    let opts = this.getOptions();
    let url = this.defaultApi;
    if (id) {
      url += '/' + id;
    }
    if (link) {
      url += '/' + link;
    }
    return this.http.delete(url, opts) as any;
  }

  protected baseGetPerPage(params: {
    page: number;
    size: number;
    customFilters: any;
    filters?: {
      [s: string]: FilterMetadata;
    };
    sortField?: string;
    sortOrder?: number;
  }): Observable<IPagingResultModel<any>> {
    let opts = this.getOptions();
    let url = this.defaultApi + '?page=' + params.page + '&size=' + params.size;
    if (params.filters) {
      let keys = Object.keys(params.filters);

      for (let propName of keys) {
        let propValue = params.filters[propName].value;
        url += '&' + propName + '=' + propValue;
      }
    }
    if (params.customFilters) {
      Object.entries(params.customFilters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            url += '&' + key + '=' + item;
          });
        } else if (value !== null) {
          url += '&' + key + '=' + value;
        }
      });
    }
    if (params.sortField) {
      url += '&sortField=' + params.sortField;
    }
    if (params.sortOrder) {
      url += '&sortOrder=' + params.sortOrder;
    }
    return this.http.get(url, opts) as any;
  }
}
